import {CommonModule} from "@angular/common"
import {AfterViewInit, Component, ElementRef, OnDestroy, inject, input, viewChild, output, ChangeDetectionStrategy} from "@angular/core"
import {Subject} from "rxjs"

export type GridSize = "large" | "medium" | "small" | "tiny"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-infinite-list",
    templateUrl: "./infinite-list.component.html",
    styleUrls: ["./infinite-list.component.scss"],
    imports: [CommonModule],
})
export class InfiniteListComponent implements AfterViewInit, OnDestroy {
    private readonly el = inject(ElementRef)

    // The grid type is used for the card based views, the table for row based display of items.
    readonly $listType = input<"grid" | "table">("grid", {alias: "listType"})
    readonly $gridSize = input<GridSize>(
        "large",
        // use 0 for no items, undefined for loading state
        {alias: "gridSize"},
    )

    // use 0 for no items, undefined for loading state
    readonly $count = input<number | undefined>(
        0,
        // called when the intersection status changes
        // true for intersecting, false for not intersecting
        {alias: "count"},
    )

    // called when the intersection status changes
    // true for intersecting, false for not intersecting
    readonly $intersectionStatusChange = input.required<Subject<boolean>>({alias: "intersectionStatusChange"})

    readonly createItemEvent = output<void>()

    // an intersection observer positioned at the bottom of the list detects when we need to load more elements
    readonly $intersectionObserverTrigger = viewChild.required<ElementRef>("cm_intersection_observer_trigger")
    private intersectionObserver!: IntersectionObserver

    ngAfterViewInit() {
        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                const latestEntry = entries?.[entries.length - 1]
                this.$intersectionStatusChange()?.next(!latestEntry?.isIntersecting)
            },
            {
                root: this.findScrollableAncestor(),
                rootMargin: "400px",
                threshold: [0, 1],
            },
        )
        this.intersectionObserver.observe(this.$intersectionObserverTrigger().nativeElement)
    }

    private findScrollableAncestor(): HTMLElement | null {
        let current = this.el.nativeElement.parentElement
        while (current) {
            const overflowY = getComputedStyle(current).overflowY
            if (overflowY === "auto" || overflowY === "scroll") {
                return current
            }
            current = current.parentElement
        }
        return null
    }

    ngOnDestroy() {
        this.intersectionObserver?.disconnect()
    }
}
